import {useAuth0} from '@auth0/auth0-react';
import {useCallback, useEffect, useMemo, useRef, useState, type ReactNode} from 'react';
import {useNavigate, useLocation} from 'react-router-dom';
import {
  devLogin,
  isLostSessionError,
  logoutApi,
  sessionAuth,
  setAccessTokenProvider
} from '../api/client';
import {REDIRECT_STORAGE_KEY} from '../lib/authKeys';
import {AuthContext, type AuthContextType, type AuthUser} from './useAuth';

const TOKEN_KEY = 'token';
const USER_KEY = 'user';
export {REDIRECT_STORAGE_KEY} from '../lib/authKeys';
export const MISSING_REFRESH_TOKEN_RETRY_KEY = 'auth0-missing-refresh-token-retry';

// Dev login keeps only its token in localStorage, never the profile (code scanning alert 2: clear-text
// storage of sensitive information). A reload rehydrates the user from /auth/session with that token
// instead - the DevAuth scheme accepts it there just as it does on every other endpoint.
function hasDevToken(): boolean {
  return import.meta.env.DEV && localStorage.getItem(TOKEN_KEY) !== null;
}

export const E2E_MOCK_USER_KEY = 'e2e-mock-user';

// Authenticated e2e coverage (docs/E2E_TEST_PLAN.md §4) has no working Auth0 fixture to drive against
// - no MSW, no test-only provider, and the real @auth0/auth0-spa-js localStorage cache format is
// internal/version-sensitive, not something to depend on. This seam is the explicit alternative:
// gated on the `e2e-mock` Vite mode (not DEV, so it can never coincide with the real dev-login gate
// above), it lets a Playwright test seed a user directly via `page.addInitScript` before the app
// mounts, without touching Auth0 or the backend at all. Never reachable in a real `pnpm build`
// (mode 'production') or `pnpm dev` (mode 'development') - only `vite build --mode e2e-mock`
// (apps/web's `build:e2e` script, used solely by playwright.config.ts's webServer).
function loadE2EMockUser(): AuthUser | null {
  if (import.meta.env.MODE !== 'e2e-mock') {
    return null;
  }

  const stored = localStorage.getItem(E2E_MOCK_USER_KEY);
  if (stored === null) {
    return null;
  }

  try {
    return JSON.parse(stored) as AuthUser;
  } catch {
    return null;
  }
}

// Auth0-spa-js's MissingRefreshTokenError sets `error: 'missing_refresh_token'` alongside the
// message shown in the screenshot this fixes.
function isMissingRefreshTokenError(error: Error | null | undefined): boolean {
  return (error as {error?: string} | null | undefined)?.error === 'missing_refresh_token';
}

function toAuthUser(response: {
  username: string;
  display_name: string;
  role: string;
  allowed_verticals: string[] | null;
  skip_p0_approval: boolean;
  permissions: string[];
}): AuthUser {
  return {
    username: response.username,
    displayName: response.display_name,
    role: response.role,
    allowedVerticals: response.allowed_verticals,
    skipP0Approval: response.skip_p0_approval,
    permissions: response.permissions
  };
}

export function AuthProvider({children}: {readonly children: ReactNode}) {
  const {
    isLoading: isAuth0Loading,
    isAuthenticated: isAuth0Authenticated,
    error: auth0Error,
    loginWithRedirect,
    logout: logoutAuth0,
    getAccessTokenSilently
  } = useAuth0();
  const [user, setUser] = useState<AuthUser | null>(loadE2EMockUser);
  // Read once, at mount: a later dev login sets the user itself and needs no rehydration.
  const [isDevSessionLoading, setIsDevSessionLoading] = useState(hasDevToken);
  const [sessionError, setSessionError] = useState<Error | null>(null);
  const [isSessionLoading, setIsSessionLoading] = useState(false);
  const [hasCheckedSession, setHasCheckedSession] = useState(false);
  // The /auth/session probe found the Auth0 session over - the everyday case of opening PRI after
  // the tenant's session ceiling has passed. Shown as the expired notice, not a sign-in failure.
  const [isSessionLost, setIsSessionLost] = useState(false);
  // Read once, at mount, and never updated afterwards: whether this mount already gets no more
  // automatic retries drives isMissingRefreshToken below, and must stay fixed for this mount's
  // whole life so a render between setting the sessionStorage marker and the browser actually
  // leaving for Auth0 can't flip the suppressed error back to showing (a plain re-read at render
  // time raced on exactly this - see PR #284 review). A genuinely new occurrence only appears on
  // the next mount, i.e. after that navigation completes, when this recomputes fresh.
  const [missingRefreshTokenAlreadyRetried] = useState(
    () => sessionStorage.getItem(MISSING_REFRESH_TOKEN_RETRY_KEY) === '1'
  );
  // One-shot guard for the effect below, so a re-run within the same mount (e.g. a location
  // change before the redirect navigates away) can't fire a second login().
  const hasFiredMissingRefreshTokenRetryRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Builds before alert 2 was fixed persisted the whole profile here; don't leave it behind.
    localStorage.removeItem(USER_KEY);
    if (!hasDevToken()) {
      return;
    }

    sessionAuth()
      .then((response) => {
        setUser(toAuthUser(response.data));
      })
      .catch(() => {
        // Expired, revoked or minted by another API instance: fall back to the login page.
        localStorage.removeItem(TOKEN_KEY);
      })
      .finally(() => {
        setIsDevSessionLoading(false);
      });
  }, []);

  useEffect(() => {
    setAccessTokenProvider(isAuth0Authenticated ? getAccessTokenSilently : null);
    return () => {
      setAccessTokenProvider(null);
    };
  }, [getAccessTokenSilently, isAuth0Authenticated]);

  // Declared before the effect below so that effect can call it directly instead of duplicating
  // its authorizationParams - keep it above that effect if you touch either.
  const login = useCallback(async () => {
    await loginWithRedirect({
      authorizationParams: {
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,
        redirect_uri: `${globalThis.location.origin}${import.meta.env.BASE_URL}`
      }
    });
  }, [loginWithRedirect]);

  useEffect(() => {
    // Rotating refresh tokens are one-time-use; a silent renewal racing across tabs can consume
    // the cached token before another tab reads it, leaving auth0-spa-js with nothing to refresh.
    // That's an expected recovery case, not a config problem, so redirect straight back through
    // login rather than surfacing "Missing Refresh Token" — an internal SDK message.
    //
    // One automatic retry per session: a genuine tab-race recovers via a fresh login. A repeat
    // means something more persistent is wrong (e.g. the tenant stopped issuing refresh tokens),
    // so surface the error instead of bouncing to Auth0 and back forever - the sessionStorage
    // marker only clears on a successful auth (below), never here, so this can't re-arm itself.
    if (
      !isMissingRefreshTokenError(auth0Error) ||
      missingRefreshTokenAlreadyRetried ||
      hasFiredMissingRefreshTokenRetryRef.current
    ) {
      return;
    }

    hasFiredMissingRefreshTokenRetryRef.current = true;
    sessionStorage.setItem(MISSING_REFRESH_TOKEN_RETRY_KEY, '1');
    // Preserve the deep link the user was heading to — with isLoading pinned true below,
    // LoginPage never mounts to write this itself the way ProtectedRoute's redirect normally does.
    // Skip it if something already stashed a target for this sign-in; that one wins.
    if (location.pathname !== '/login' && sessionStorage.getItem(REDIRECT_STORAGE_KEY) === null) {
      sessionStorage.setItem(REDIRECT_STORAGE_KEY, `${location.pathname}${location.search}`);
    }

    void login();
  }, [auth0Error, missingRefreshTokenAlreadyRetried, login, location.pathname, location.search]);

  useEffect(() => {
    if (isAuth0Loading) {
      return;
    }

    if (!isAuth0Authenticated) {
      setHasCheckedSession(false);
      return;
    }

    // Only matters for a future mount: missingRefreshTokenAlreadyRetried above is frozen for
    // this one's whole life.
    sessionStorage.removeItem(MISSING_REFRESH_TOKEN_RETRY_KEY);
    setIsSessionLoading(true);
    setSessionError(null);
    sessionAuth()
      .then((response) => {
        setIsSessionLost(false);
        setUser(toAuthUser(response.data));
      })
      .catch((error_: unknown) => {
        setUser(null);
        // The SDK's own `error` (useAuth0) is only set from a redirect callback or popup, so on a
        // page load a dead refresh token arrives here, thrown by getAccessTokenSilently in the
        // request interceptor. Its ID token is still cached, which is why Auth0 reported the user as
        // signed in; clearing it locally (no redirect to Auth0) stops every later load repeating this.
        if (isLostSessionError(error_)) {
          setIsSessionLost(true);
          void logoutAuth0({openUrl: false});
          return;
        }

        setSessionError(
          error_ instanceof Error ? error_ : new Error('Failed to load user session.')
        );
      })
      .finally(() => {
        setIsSessionLoading(false);
        setHasCheckedSession(true);
      });
  }, [isAuth0Authenticated, isAuth0Loading, logoutAuth0]);

  useEffect(() => {
    if (user !== null && location.pathname !== '/login') {
      const target = sessionStorage.getItem(REDIRECT_STORAGE_KEY);
      if (target) {
        sessionStorage.removeItem(REDIRECT_STORAGE_KEY);
        void navigate(target, {replace: true});
      }
    }
  }, [user, navigate, location.pathname]);

  const loginWithDevAuth = useCallback(async (email: string, displayName?: string) => {
    const response = await devLogin(email, displayName);
    const userData = toAuthUser(response.data);
    localStorage.setItem(TOKEN_KEY, response.data.token);
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    logoutApi().catch(() => undefined);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);

    if (isAuth0Authenticated) {
      void logoutAuth0({
        logoutParams: {
          returnTo: `${globalThis.location.origin}${import.meta.env.BASE_URL}`
        }
      });
    }
  }, [isAuth0Authenticated, logoutAuth0]);

  // Only the first occurrence this session is suppressed/auto-retried (see the effect above); a
  // second one stops pinning isLoading and lands on LoginPage as an expired-session notice - never
  // as the SDK's raw "Missing Refresh Token" message, which means nothing to an analyst.
  const isMissingRefreshToken =
    isMissingRefreshTokenError(auth0Error) && !missingRefreshTokenAlreadyRetried;
  const sessionExpired =
    isSessionLost || (isMissingRefreshTokenError(auth0Error) && missingRefreshTokenAlreadyRetried);

  const value = useMemo<AuthContextType>(
    () => ({
      isAuthenticated: user !== null,
      // Redirecting away, not an error state worth flashing on screen.
      isLoading:
        isAuth0Loading ||
        isSessionLoading ||
        isDevSessionLoading ||
        (isAuth0Authenticated && !hasCheckedSession) ||
        isMissingRefreshToken,
      user,
      error: isMissingRefreshTokenError(auth0Error) ? null : (auth0Error ?? sessionError ?? null),
      sessionExpired,
      login,
      loginWithDevAuth,
      logout
    }),
    [
      isAuth0Authenticated,
      isAuth0Loading,
      auth0Error,
      isMissingRefreshToken,
      sessionExpired,
      sessionError,
      isSessionLoading,
      isDevSessionLoading,
      hasCheckedSession,
      user,
      login,
      loginWithDevAuth,
      logout
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
